<script setup lang="ts">
import { ref, computed } from 'vue'

import { useRouter } from 'vue-router'

import { useEventListener } from '@vueuse/core'
import { nanoid } from 'nanoid'

import FrontendHeader from '@/components/global/FrontendHeader.vue'
import FrontendSidebar from '@/components/global/FrontendSidebar.vue'

import { useConfirmDialog } from '@/composables/useConfirmDialog'
import { useSSOAccounts } from '@/composables/useSSOAccounts'

const router = useRouter()

// 需要 Keep Alive 的組件
const keepAliveKey = ref(nanoid())

// 從 router meta 動態取得需要 keep-alive 的組件名稱
const cachedRouteList = computed(() => {
  return router
    .getRoutes()
    .filter((route) => route.meta?.keepAlive === true)
    .map((route) => route.meta?.componentName)
    .filter((name): name is string => typeof name === 'string')
})

// 監聽 message 事件
const { handleBindSSOAccount } = useSSOAccounts()

const handleMessage = async (event: MessageEvent) => {
  const allowedOrigin = window.location.origin
  if (event.origin !== allowedOrigin) {
    console.warn('Received message from unknown origin:', event.origin)
    return
  }

  const { access_token, errorMsg } = event.data
  if (access_token) {
    await handleBindSSOAccount(access_token)
    return
  }
  if (errorMsg) {
    const { alert } = useConfirmDialog()
    alert({
      title: '發生錯誤',
      text: errorMsg || '發生未知錯誤，請稍後再試。',
      type: 'danger',
      confirmButtonText: '確定'
    })
    return
  }
}

useEventListener(window, 'message', handleMessage)
</script>
<template>
  <div>
    <FrontendHeader />
    <div class="flex h-[calc(100vh-80px)]">
      <FrontendSidebar />
      <div class="min-w-0 flex-grow overflow-y-auto bg-app-primary-blue-10 px-4 md:pl-9 md:pr-10">
        <router-view v-slot="{ Component }">
          <KeepAlive :key="keepAliveKey" :include="cachedRouteList">
            <component :is="Component" />
          </KeepAlive>
        </router-view>
      </div>
    </div>
  </div>
</template>
<style scoped></style>
